import { Product } from '@@types'
import { URL_PAGE_PRODUCT } from '@hive/platform-url-schema'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'

import { BRAND_CONFIG } from '@@brand'
import { useCartActions, useCartStore } from '@entities/Cart'

import { useAuth } from '@features/auth'
import { CartCommentButton, CartDeleteProductButton, useCartItemTotal } from '@features/cart'
import ControlCart from '@features/cart/ui/control/CartControl'
import { FavoriteButton } from '@features/favorite'
import { InStock } from '@features/in-stock'
import { useErrorInfoModal } from '@features/modal'

import { SimilarProductsTrigger } from '@widgets/products-similar'

import { useProductUnit } from '@shared/hooks'
import { useAnalytics } from '@shared/lib/analytics'
import { cn } from '@shared/lib/cn'
import { LocalizedLink as Link } from '@shared/lib/locale'
import ImageWithBg from '@shared/ui/image-with-bg'
import { formatPrice } from '@shared/uttils'

import styles from './ProductCart.module.scss'

export interface ProductCartCardProps {
  data: Product;
  amount: number;
  className?: string;
  outStockProduct?: boolean;
}

function ProductCart (props: ProductCartCardProps) {
  const { openErrorInfoModal } = useErrorInfoModal()
  const { data, className, amount, outStockProduct } = props
  const isTablet = useMediaQuery('(max-width: 991px)')
  const isMobileSm = useMediaQuery('(max-width: 375px)')
  const { removeProductImmediately, cancelPendingRemoval } = useCartActions()
  const pendingRemovals = useCartStore((state) => state.pendingRemovals)
  const removingProducts = useCartStore((state) => state.removingProducts)
  const { isAuthorized } = useAuth()
  const { t: tc } = useTranslation('common')
  const { t: tca } = useTranslation('cart')
  const { t: te } = useTranslation('errors')
  const cartItemTotalPrice = useCartItemTotal(data, amount)
  const cartItemTotalOldPrice = useCartItemTotal(data, amount, true)
  const isPendingRemoval = pendingRemovals.has(String(data.id))
  const isRemoving = removingProducts.has(String(data.id))
  const { removeFromCart: trackRemoveFromCart } = useAnalytics()

  const handleDelete = async () => {
    // Track analytics: removing product from cart
    trackRemoveFromCart(data, amount)
    
    try {
      await removeProductImmediately(data.id, isAuthorized)
    }
    catch (error) {
      console.error('Failed to delete item from cart:', error)
      openErrorInfoModal({
        description: te('drop_cart_item'),
      })
    }
  }

  const handleRestore = () => {
    cancelPendingRemoval(String(data.id))
  }

  const { unit } = useProductUnit({
    isWeightProduct: data.isWeightProduct as boolean,
    value: amount,
    price: cartItemTotalPrice as number,
    t: tc,
  })

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (outStockProduct) {
      e.preventDefault()
    }
  }

  return (
    <div
      className={cn(styles.root, className, {
        [styles.out]: outStockProduct,
        [styles['pending-removal']]: isPendingRemoval,
        [styles.removing]: isRemoving,
      })}
    >
      <Link
        to={generatePath(URL_PAGE_PRODUCT, {
          slug: data.slug
        })}
        className={styles.root__image}
        onClick={handleLinkClick}
      >
        <ImageWithBg
          className={styles['root__image-inner']}
          src={data.preview?.url || BRAND_CONFIG.images.productPlaceholder}
        />
      </Link>

      <div className={styles.root__info}>
        <Link
          to={`/product/${data.slug}`}
          className={styles.root__name}
          onClick={handleLinkClick}
        >
          {data.name}
        </Link>

        <div className={styles.root__stock}>
          <InStock data={data} />
        </div>

        {!isPendingRemoval && !isRemoving && !outStockProduct && (
          <ControlCart
            classNameControl={styles.root__control}
            isTablet={isTablet}
            product={data}
            size="small"
          />
        )}
        {isPendingRemoval && !outStockProduct && (
          <div className={styles.root__pending}>
            <button
              className={styles['root__restore-btn']}
              onClick={handleRestore}
            >
              {tca('restore')}
            </button>
          </div>
        )}
        {isRemoving && !outStockProduct && (
          <div className={styles.root__removing}>
            <span className={styles['root__spinner']} />
            <span className={styles['root__removing-text']}>
              {tca('deleting')}
            </span>
          </div>
        )}

        {!outStockProduct && (
          <div className={styles['root__info-wrapper']}>
            <div className={styles.root__price}>
              {cartItemTotalPrice && (
                <div className={styles['root__price-value']}>
                  {formatPrice(cartItemTotalPrice)}
                </div>
              )}
              {!!data.priceOffer?.oldPrice && cartItemTotalOldPrice && (
                <div className={styles['root__price-old']}>
                  {formatPrice(cartItemTotalOldPrice)}
                </div>
              )}
            </div>

            <span className={styles.root__count}>
              {amount} {unit} x {formatPrice(data.priceOffer?.price)}
            </span>
          </div>
        )}

        {!outStockProduct && !isRemoving && (
          <div className={styles['root__btn-group']}>
            {isMobileSm ? (
              <>
                <FavoriteButton id={data.id} isLarge product={data} />
                <CartDeleteProductButton onClick={handleDelete} />
                <CartCommentButton product={data} />
              </>
            ) : (
              <>
                <CartCommentButton product={data} />
                <FavoriteButton id={data.id} isLarge product={data} />
                <CartDeleteProductButton onClick={handleDelete} />
              </>
            )}
          </div>
        )}

        {outStockProduct && (
          <div className={styles['root__btn-group']}>
            <SimilarProductsTrigger product={data} />

            <CartDeleteProductButton
              onClick={async () => {
                try {
                  await removeProductImmediately(data.id, isAuthorized)
                }
                catch (error) {
                  console.error('removeProductImmediately error: ', error)
                  openErrorInfoModal({
                    description: te('drop_cart_item'),
                  })
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default memo(ProductCart)
